import { useEffect, useRef, type ReactNode } from 'react'
import type { DomainDef, IndexDef, LevelCode, ObjectiveDef, ObjectiveState, Snapshot } from '@/api/types/readiness'
import { levelNum } from '@/api/types/readiness'
import { domainColor } from '@/lib/indexes/registry'
import { liveLevel, liveScore, needsConfirmation, needsScoreConfirmation } from '@/lib/readiness/score'
import { assertScale, fmtScore } from '@/lib/readiness/scale'
import { cellLevel, remoteSourceLabel, undeterminedReason, type AssessmentCell } from '@/api/clients/assessmentsApi'
import { Chip, Icon } from '@/components/readiness/primitives'
import { Term } from '@/components/readiness/Term'

// Heatmap view (I2 §4): one card, a grid of all objectives, rows per domain,
// level-tinted cells. No red anywhere: L0 is neutral grey. Domain colours appear
// only in the row label chips. Filtered-out cells dim to 40% so the grid keeps
// its shape. Clicking a cell opens the quick view drawer (§5).

export interface HeatmapCellData {
  domain: DomainDef
  objective: ObjectiveDef
  state: ObjectiveState | undefined
  evidenceCount: number
  primarySource: string | null
  visible: boolean
  // The readiness service's cell; when set it replaces state, evidence and the
  // last-assessment delta, and the level tints still come from the theme tokens.
  remote?: AssessmentCell
}

// What a cell shows, from the in-browser engine or from the readiness service.
interface CellView {
  unset: boolean
  level: LevelCode
  score: number | null
  pending: boolean
  delta: { text: string; tone: 'ok' | 'warn' | 'neutral' } | null
  evidenceCount: number
  primarySource: string | null
}

const TINT: Record<LevelCode, { bg: string; edge: string; text: string }> = {
  L0: { bg: 'var(--rd-tint-l0)', edge: 'var(--rd-tint-l0-edge)', text: 'var(--rd-text-muted)' },
  L1: { bg: 'var(--rd-tint-l1)', edge: 'var(--rd-tint-l1-edge)', text: 'var(--rd-level-l1)' },
  L2: { bg: 'var(--rd-tint-l2)', edge: 'var(--rd-tint-l2-edge)', text: 'var(--rd-tint-l2-text)' },
  L3: { bg: 'var(--rd-tint-l3)', edge: 'var(--rd-tint-l3-edge)', text: 'var(--rd-level-l3)' },
}

// Score 0–10 → the nearest level tint (0–2.5 L0, 2.5–5 L1, 5–7.5 L2, 7.5–10 L3).
const tintForScore = (s: number | null): LevelCode => (s == null ? 'L0' : s >= 7.5 ? 'L3' : s >= 5 ? 'L2' : s >= 2.5 ? 'L1' : 'L0')

export function Heatmap({ index, cells, theme, lastAssessment, selected, onSelect, caption }: { index: IndexDef; cells: HeatmapCellData[]; theme: 'light' | 'dark'; lastAssessment: Snapshot | null; selected: string | null; onSelect: (code: string) => void; caption?: ReactNode }) {
  const scale = assertScale(index)
  const domains = [...index.domains].sort((a, b) => a.order - b.order)
  const cols = Math.max(1, ...domains.map((d) => d.objectives.length))
  const refs = useRef<Record<string, HTMLButtonElement | null>>({})
  // Scroll the selected cell into view when Prev/Next lands on it (§5.1).
  useEffect(() => {
    if (!selected) return
    const el = refs.current[selected]
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [selected])
  const header = (i: number) => {
    const noRegrets = i === 0 && index.starting_set?.render === 'filter'
    return (
      <div key={i} className="rd-label flex items-center gap-1.5 px-1" style={{ color: noRegrets ? 'var(--rd-accent-chip-fg)' : undefined }}>
        {noRegrets ? (
          <Term index={index} term="No-regrets" className="!no-underline">
            <span>★ {scale === 'levels' ? 'Objective' : 'Sub-index'} 1 · No-regrets</span>
          </Term>
        ) : (
          <span>{scale === 'levels' ? 'Objective' : 'Sub-index'} {i + 1}</span>
        )}
      </div>
    )
  }
  return (
    <section className="rd-card p-5" aria-label="All objectives at a glance" data-testid="heatmap">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="rd-section-title">All {index.domains.reduce((a, d) => a + d.objectives.length, 0) === 10 ? 'ten' : index.domains.reduce((a, d) => a + d.objectives.length, 0)} {scale === 'levels' ? 'objectives' : 'sub-indices'} at a glance</p>
          <p className="mt-0.5 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>Select a cell for a quick view; open the objective for evidence, next steps and history.</p>
          {caption && <p className="mt-1 text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }} data-testid="heatmap-source">{caption}</p>}
        </div>
        <ul className="flex flex-wrap items-center gap-3 text-[11.5px]" style={{ color: 'var(--rd-text-muted)' }} aria-label="Legend">
          <li className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm" style={{ border: '1.5px dashed var(--rd-tint-l0-edge)' }} /> Not set</li>
          {(['L0', 'L1', 'L2', 'L3'] as LevelCode[]).map((l) => (
            <li key={l} className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm" style={{ background: TINT[l].bg, borderLeft: `3px solid ${TINT[l].edge}` }} /> <span className="rd-mono">{scale === 'levels' ? l : `${levelNum(l) * 2.5}–${levelNum(l) * 2.5 + 2.5}`}</span></li>
          ))}
        </ul>
      </div>
      <div className="mt-4 overflow-x-auto">
        <div className="grid gap-x-3 gap-y-2" style={{ gridTemplateColumns: `200px repeat(${cols}, minmax(160px, 1fr))`, minWidth: 200 + cols * 170 }}>
          <div />
          {Array.from({ length: cols }, (_, i) => header(i))}
          {domains.map((d) => {
            const color = domainColor(d, theme)
            return [
              <div key={`${d.code}-label`} className="flex items-center gap-2 pr-2">
                <span className="rd-chip" style={{ background: `${color}22`, color, fontWeight: 700, letterSpacing: '0.06em' }}>{d.code}</span>
                <span className="min-w-0 truncate text-[14px] font-medium">{d.name}</span>
              </div>,
              ...Array.from({ length: cols }, (_, i) => {
                const o = d.objectives[i]
                if (!o) return <div key={`${d.code}-${i}`} />
                const cell = cells.find((c) => c.objective.code === o.code)!
                return <Cell key={o.code} index={index} cell={cell} color={color} lastAssessment={lastAssessment} active={selected === o.code} onSelect={onSelect} refCb={(el) => (refs.current[o.code] = el)} />
              }),
            ]
          })}
        </div>
      </div>
    </section>
  )
}

// In-browser engine: the live state, delta chip vs the last saved assessment (§4.1).
function engineView(index: IndexDef, cell: HeatmapCellData, lastAssessment: Snapshot | null): CellView {
  const scale = assertScale(index)
  const { objective, state } = cell
  const unset = !state || state.status === 'unset'
  const level = liveLevel(state)
  const score = liveScore(state)
  const prev = lastAssessment?.items.find((i) => i.objective_code === objective.code)
  const pending = scale === 'levels' ? needsConfirmation(state) : needsScoreConfirmation(state)
  let delta: CellView['delta'] = null
  if (!unset && lastAssessment) {
    if (scale === 'levels') {
      const was = prev?.level ?? 'L0'
      delta = levelNum(level) > levelNum(was) ? { text: `↑ was ${was}`, tone: 'ok' } : levelNum(level) < levelNum(was) ? { text: `↓ was ${was}`, tone: 'warn' } : { text: 'unchanged', tone: 'neutral' }
    } else {
      const was = prev?.score ?? null
      delta = was == null ? { text: 'new', tone: 'neutral' } : (score ?? 0) > was ? { text: `↑ was ${fmtScore(was)}`, tone: 'ok' } : (score ?? 0) < was ? { text: `↓ was ${fmtScore(was)}`, tone: 'warn' } : { text: 'unchanged', tone: 'neutral' }
    }
  }
  return { unset, level, score, pending, delta, evidenceCount: cell.evidenceCount, primarySource: cell.primarySource }
}

// Readiness service: the cell as served, delta chip vs the previous assessment.
function remoteView(c: AssessmentCell): CellView {
  const was = `L${c.previous_level}`
  const delta: CellView['delta'] = c.movement === 'up' ? { text: `↑ was ${was}`, tone: 'ok' } : c.movement === 'down' ? { text: `↓ was ${was}`, tone: 'warn' } : c.movement === 'unchanged' ? { text: 'unchanged', tone: 'neutral' } : null
  return { unset: c.level == null, level: cellLevel(c), score: null, pending: c.needs_confirmation, delta, evidenceCount: c.evidence_count, primarySource: remoteSourceLabel(c) }
}

function Cell({ index, cell, color, lastAssessment, active, onSelect, refCb }: { index: IndexDef; cell: HeatmapCellData; color: string; lastAssessment: Snapshot | null; active: boolean; onSelect: (code: string) => void; refCb: (el: HTMLButtonElement | null) => void }) {
  const scale = assertScale(index)
  const { objective } = cell
  const { unset, level, score, pending, delta, evidenceCount, primarySource } = cell.remote ? remoteView(cell.remote) : engineView(index, cell, lastAssessment)
  // A served cell waiting on a sync suggestion says which level is suggested.
  const suggestionText = cell.remote?.review_reason === 'suggestion' && cell.remote.suggested_level != null ? `Review: suggests L${cell.remote.suggested_level}` : null
  // A sync that found too little suggests no level; the cell says so.
  const undetermined = !!cell.remote && !!undeterminedReason(cell.remote)
  const tint = TINT[scale === 'levels' ? level : tintForScore(score)]
  const big = scale === 'levels' ? level : fmtScore(score)
  const label = unset ? '○ Not set' : big
  return (
    <button
      ref={refCb}
      type="button"
      onClick={() => onSelect(objective.code)}
      aria-pressed={active}
      aria-label={`${objective.code} · ${objective.title} · ${unset ? 'not set' : big}${pending ? ' · needs confirmation' : ''}`}
      data-testid={`heatmap-cell-${objective.code}`}
      className={`relative flex min-h-[72px] flex-col justify-between rounded-[10px] p-2.5 text-left transition-opacity ${active ? 'rd-cell-active' : ''}`}
      style={{
        background: unset ? 'transparent' : tint.bg,
        border: unset ? '1.5px dashed var(--rd-tint-l0-edge)' : '1px solid transparent',
        borderLeft: unset ? '1.5px dashed var(--rd-tint-l0-edge)' : `3px solid ${tint.edge}`,
        opacity: cell.visible ? 1 : 0.4,
        ['--rd-cell-ring' as string]: color,
        ['--rd-cell-glow' as string]: `${color}1f`,
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="rd-mono font-bold leading-none" style={{ color: unset ? 'var(--rd-text-muted)' : tint.text, fontSize: unset ? 14 : 28 }}>{label}</span>
        {delta && (
          <Chip tone={delta.tone === 'ok' ? 'ok' : delta.tone === 'warn' ? 'warn' : 'neutral'}>{delta.text}</Chip>
        )}
      </div>
      <div className="mt-2 flex items-end justify-between gap-2">
        <span className="min-w-0 truncate text-[11.5px]" style={{ color: 'var(--rd-text-muted)' }}>
          <span className="rd-mono" style={{ color: 'var(--rd-text-secondary)' }}>{objective.code}</span> ·{' '}
          {pending ? <span style={{ color: 'var(--rd-status-warn)' }}>⚠ {suggestionText ?? 'Needs confirmation'}</span> : undetermined ? <span data-testid={`undetermined-${objective.code}`}>Unable to determine</span> : unset ? 'Manual input pending' : `${evidenceCount} evidence${primarySource ? ` · ${primarySource}` : ''}`}
        </span>
        <Icon name="chevron-right" size={14} style={{ color: 'var(--rd-text-faint)' }} />
      </div>
    </button>
  )
}
